import { useState, useCallback } from 'react';
import { useLazyQuery } from '@apollo/client';
import { QUERY_EXCEPTION_ORDER_COUNTS_BY_TABS } from '../graphql/queries/exceptionOrderQueries';
import { ExceptionOrderFilters } from '../../../../graphql/types/ExceptionOrderTypes';

interface TabCountsState {
  warehouseSuspectedLost: number | undefined;
  driverReturn: number | undefined;
  otherExceptions: number | undefined;
  // 司机退回子tab计数
  driverReturnSubTabs: {
    all: number | undefined;
    pendingPutaway: number | undefined;
    putaway: number | undefined;
    pendingPicking: number | undefined;
    picked: number | undefined;
  };
  // 其他异常子tab计数
  otherExceptionsSubTabs: {
    missing: number | undefined;
    longMissing: number | undefined;
    damaged: number | undefined;
    missort: number | undefined;
    oversize: number | undefined;
  };
}

interface UseExceptionOrderCountsResult {
  tabCounts: TabCountsState;
  loadingCounts: boolean;
  refreshAllCounts: (filters: ExceptionOrderFilters, activeTab?: string) => void;
  updateCurrentTabCount: (tab: string, count: number) => void;
  refreshSubTabCounts: (filters: ExceptionOrderFilters, mainTab: string) => void;
}

// 主tab与其子tab的映射；无子tab的主tab不在此表中
const SUB_TABS_BY_MAIN_TAB: Record<string, string[]> = {
  DRIVER_RETURN: [
    'DRIVER_RETURN_ALL',
    'DRIVER_RETURN_PENDING_PUTAWAY',
    'DRIVER_RETURN_PUTAWAY',
    'DRIVER_RETURN_PENDING_PICKING',
    'DRIVER_RETURN_PICKED',
  ],
  OTHER_EXCEPTIONS: [
    'OTHER_MISSING',
    'OTHER_LONG_MISSING',
    'OTHER_DAMAGED',
    'OTHER_MISSORT',
    'OTHER_OVERSIZE',
  ],
};

// 司机退回主tab计数与「全部退回」子tab计数在后端是同一个查询
// （getMainTabCount 对 DRIVER_RETURN 强制使用 DRIVER_RETURN_ALL 预设），
// 故展开司机退回时主tab计数直接复用子tab结果，不重复查询。
const MAIN_TABS = ['WAREHOUSE_SUSPECTED_LOST', 'DRIVER_RETURN', 'OTHER_EXCEPTIONS'];

/**
 * 将 GraphQL 返回的计数列表转为 map
 */
const toCountMap = (
  counts: Array<{ tab: string; count: number | string }> | undefined
): Record<string, number> => {
  const countMap: Record<string, number> = {};
  (counts ?? []).forEach((item) => {
    const numericCount = typeof item.count === 'string' ? parseInt(item.count, 10) : item.count;
    countMap[item.tab] = isNaN(numericCount) ? 0 : numericCount;
  });
  return countMap;
};

/**
 * 手动管理的异常单计数Hook
 *
 * 特点：
 * 1. 移除所有useEffect依赖，完全手动管理状态
 * 2. 简化计数逻辑，避免意外的状态更新
 * 3. 提供明确的手动刷新方法
 * 4. 消除tab切换时的计数闪烁问题
 *
 * 请求收敛策略（接口本身支持 tabs 批量，不必一个tab一个请求）：
 * - 主tab计数：3个主tab合并为 1 个请求
 * - 子tab计数：仅查询当前展开主tab的子tab（其它主tab的子tab标签不可见，查了也没人看）
 * - 司机退回主tab复用 DRIVER_RETURN_ALL 结果，不单独查询
 */
export const useExceptionOrderCounts = (warehouseId: number): UseExceptionOrderCountsResult => {
  const [tabCounts, setTabCounts] = useState<TabCountsState>({
    warehouseSuspectedLost: undefined,
    driverReturn: undefined,
    otherExceptions: undefined,
    driverReturnSubTabs: {
      all: undefined,
      pendingPutaway: undefined,
      putaway: undefined,
      pendingPicking: undefined,
      picked: undefined,
    },
    otherExceptionsSubTabs: {
      missing: undefined,
      longMissing: undefined,
      damaged: undefined,
      missort: undefined,
      oversize: undefined,
    },
  });

  const [loadingCounts, setLoadingCounts] = useState(false);

  // 计数查询 - 使用no-cache策略避免缓存问题
  const [executeCountQuery] = useLazyQuery(QUERY_EXCEPTION_ORDER_COUNTS_BY_TABS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  // 通用函数：构建共享的基础筛选条件
  const buildBaseFilters = useCallback((filters: ExceptionOrderFilters) => ({
    trackingNumbers: filters.trackingNumbers,
    overallStatus: filters.overallStatus,
    clientId: filters.clientId,
    exceptionStatus: filters.exceptionStatus,
    nextActions: filters.nextActions,
    markExceptionAtFrom: filters.markExceptionAtFrom,
    markExceptionAtTo: filters.markExceptionAtTo,
    returnToWhsAtFrom: filters.returnToWhsAtFrom,
    returnToWhsAtTo: filters.returnToWhsAtTo,
    putawayAtFrom: filters.putawayAtFrom,
    putawayAtTo: filters.putawayAtTo,
    warehouseLocationCode: filters.warehouseLocationCode,
    storageNumber: filters.storageNumber,
  }), []);

  /**
   * 刷新主tab计数
   *
   * activeTab 传入当前展开的主tab时，会跳过它自己的子tab等价查询：
   * 司机退回展开时其主tab计数由 refreshSubTabCounts 的 DRIVER_RETURN_ALL 回填。
   */
  const refreshAllCounts = useCallback((filters: ExceptionOrderFilters, activeTab?: string) => {
    // 司机退回展开时，主tab计数由子tab查询回填，此处不重复查询
    const tabsToQuery = activeTab === 'DRIVER_RETURN'
      ? MAIN_TABS.filter(tab => tab !== 'DRIVER_RETURN')
      : MAIN_TABS;

    //立即清除主tab计数，显示加载中（子tab计数由 refreshSubTabCounts 自己管理，此处不动）
    setTabCounts(prev => ({
      ...prev,
      warehouseSuspectedLost: undefined,
      // 司机退回不查时保留现值，避免标签闪烁成 (...)
      driverReturn: activeTab === 'DRIVER_RETURN' ? prev.driverReturn : undefined,
      otherExceptions: undefined,
    }));

    setLoadingCounts(true);

    // 3个主tab合并为 1 个请求（接口支持 tabs 批量，后端按 tabKey 各自解析预设条件）
    executeCountQuery({
      variables: {
        warehouseId,
        filters: buildBaseFilters(filters),
        tabs: tabsToQuery,
      }
    }).then((result) => {
      setLoadingCounts(false);

      if (result.error && !result.data?.queryExceptionOrderCountsByTabs) {
        console.error('[Count Error] Failed to get main tab counts:', result.error);
        // 整个请求失败时全部回退到 0，避免标签永久停留在 (...)
        setTabCounts(prev => ({
          ...prev,
          warehouseSuspectedLost: prev.warehouseSuspectedLost ?? 0,
          driverReturn: prev.driverReturn ?? 0,
          otherExceptions: prev.otherExceptions ?? 0,
        }));
        return;
      }

      const countMap = toCountMap(result.data?.queryExceptionOrderCountsByTabs);

      // 手动更新所有主tab计数，确保所有tab都有值
      setTabCounts(prev => ({
        ...prev,
        warehouseSuspectedLost: countMap['WAREHOUSE_SUSPECTED_LOST'] ?? prev.warehouseSuspectedLost ?? 0,
        driverReturn: countMap['DRIVER_RETURN'] ?? prev.driverReturn ?? 0,
        otherExceptions: countMap['OTHER_EXCEPTIONS'] ?? prev.otherExceptions ?? 0,
      }));
    }).catch((error) => {
      setLoadingCounts(false);
      console.error('Count query failed:', error);
      // 网络错误时保持原有计数，避免显示undefined
    });
  }, [executeCountQuery, buildBaseFilters, warehouseId]);

  /**
   * 手动更新当前tab的计数（从列表查询结果立即更新）
   */
  const updateCurrentTabCount = useCallback((tab: string, count: number) => {
    const tabKey = getTabKey(tab);
    if (!tabKey) {
      console.warn('updateCurrentTabCount - Invalid tab key:', tab);
      return;
    }

    setTabCounts(prev => ({
      ...prev,
      [tabKey]: count
    }));
  }, []);

  /**
   * 手动刷新指定主tab的所有子tab计数
   *
   * 只在该主tab处于展开状态时调用 —— 未展开的主tab其子tab标签不渲染，查询无意义。
   */
  const refreshSubTabCounts = useCallback((filters: ExceptionOrderFilters, mainTab: string) => {
    const subTabs = SUB_TABS_BY_MAIN_TAB[mainTab];
    if (!subTabs) {
      // 无子tab的主tab（如仓库疑似丢失），不发请求
      return;
    }

    //立即清除对应子tab计数，显示加载中
    if (mainTab === 'DRIVER_RETURN') {
      setTabCounts(prev => ({
        ...prev,
        driverReturnSubTabs: {
          all: undefined,
          pendingPutaway: undefined,
          putaway: undefined,
          pendingPicking: undefined,
          picked: undefined,
        }
      }));
    } else {
      setTabCounts(prev => ({
        ...prev,
        otherExceptionsSubTabs: {
          missing: undefined,
          longMissing: undefined,
          damaged: undefined,
          missort: undefined,
          oversize: undefined,
        }
      }));
    }

    executeCountQuery({
      variables: {
        warehouseId,
        filters: {
          ...buildBaseFilters(filters),
          tab: mainTab // 确保传递主tab信息
        },
        tabs: subTabs
      }
    }).then((result) => {
      if (!result.data?.queryExceptionOrderCountsByTabs) {
        return;
      }
      const countMap = toCountMap(result.data.queryExceptionOrderCountsByTabs);

      if (mainTab === 'DRIVER_RETURN') {
        setTabCounts(prev => ({
          ...prev,
          // 司机退回主tab计数与「全部退回」等价，直接复用，无需单独查询
          driverReturn: countMap['DRIVER_RETURN_ALL'] ?? 0,
          driverReturnSubTabs: {
            all: countMap['DRIVER_RETURN_ALL'] ?? 0,
            pendingPutaway: countMap['DRIVER_RETURN_PENDING_PUTAWAY'] ?? 0,
            putaway: countMap['DRIVER_RETURN_PUTAWAY'] ?? 0,
            pendingPicking: countMap['DRIVER_RETURN_PENDING_PICKING'] ?? 0,
            picked: countMap['DRIVER_RETURN_PICKED'] ?? 0,
          }
        }));
      } else {
        setTabCounts(prev => ({
          ...prev,
          otherExceptionsSubTabs: {
            missing: countMap['OTHER_MISSING'] ?? 0,
            longMissing: countMap['OTHER_LONG_MISSING'] ?? 0,
            damaged: countMap['OTHER_DAMAGED'] ?? 0,
            missort: countMap['OTHER_MISSORT'] ?? 0,
            oversize: countMap['OTHER_OVERSIZE'] ?? 0,
          }
        }));
      }
    }).catch((error) => {
      console.error(`Failed to refresh sub-tab counts for ${mainTab}:`, error);
    });
  }, [executeCountQuery, buildBaseFilters, warehouseId]);

  return {
    tabCounts,
    loadingCounts,
    refreshAllCounts,
    updateCurrentTabCount,
    refreshSubTabCounts,
  };
};

/**
 * 将tab字符串转换为状态键
 */
function getTabKey(tab: string): keyof TabCountsState | null {
  switch (tab) {
    case 'WAREHOUSE_SUSPECTED_LOST':
      return 'warehouseSuspectedLost';
    case 'DRIVER_RETURN':
      return 'driverReturn';
    case 'OTHER_EXCEPTIONS':
      return 'otherExceptions';
    default:
      return null;
  }
}
